import React, { useCallback, useEffect, useRef, useState } from "react";
import Cookie from "js-cookie";
import { Carousel } from "@bloomberg/intercom";
import { Placeholder } from "@bloomberg/media-ui-article";
import { createClasses, Image } from "@bloomberg/media-ui-core";
import { ChevronDownThin, ChevronUpThin } from "@bloomberg/media-ui-icons";
import { useFortress } from "@foundation/fortress";

import { Story } from "~types/types";
import { convertMillisecondsToMinutesAndSecondsString } from "~utils/durationUtil";
import { getFortressState } from "~utils/getFortressState";
import { getPersonalizationSitesOverride } from "~utils/getPersonalizationEyebrowText";
import { TranslatedMessage, useTranslateMessage } from "~utils/i18n/translate";
import { capitalizeDashedString } from "~utils/stringUtil";
import { Tracker } from "~utils/tracker";

import { MobileCarousel } from "./components/MobileCarousel";
import { PlayIcon, Lock, Sparkle } from "./constants";
import { PersonalizationStory } from "./PersonalizationStory";
import styles from "./styles.module.css";
import { getCards } from "./utils/getCards";
import { getPersonalizedStories } from "./utils/getPersonalizedStories";
import { getVideoPersonalizedStories } from "./utils/getVideoPersonalizedStories";
import { useGTMNavButtonsEffect } from "./utils/useGTMNavButtonsEffect";
import { useGTMSwipeEffect } from "./utils/useGTMSwipeEffect";

interface TakeawaysProps {
    story: Story;
    isFortressBlocked?: boolean;
    isFortressLoading?: boolean;
}
const tracker = new Tracker({
    event: "Takeaways",
    action: "click",
});

const MAX_PERSONALIZATION_HEADLINES = 2;

export enum RenderCardType {
    AI_SUMMARY = "AI_SUMMARY",
    VIDEO = "VIDEO",
    PERSONALIZATION = "PERSONALIZATION",
    LOADING = "LOADING",
}
const AISummaryCard = ({ aiSummaryText }: { aiSummaryText: string[] }) => (
    <div className={styles.card} data-testid="carousel-summary-card">
        <ul className={styles.textList}>
            {aiSummaryText.map((text, index) => (
                <li className={styles.textListItem} key={index}>
                    {text}
                </li>
            ))}
        </ul>
        <span className={styles.summaryLabel}>{"Summary by Bloomberg AI"}</span>
    </div>
);

const VideoCard = ({ videos }: { videos: PersonalizationStory[] }) => {
    const video = videos[0];

    const onClick = useCallback(() => {
        tracker.trackEvent({
            category: "takeaways-card",
            label: "click-video",
        });
    }, []);

    return (
        <div className={styles.card} data-testid="carousel-video-card">
            <h3 className={styles.cardTitle}>Watch</h3>
            <a
                href={video.url}
                className={styles.videoCard}
                target="_blank"
                onClick={onClick}
            >
                <div className={styles.thumbnailContainer}>
                    <Image
                        isLazy
                        className={styles.thumbnail}
                        src={video?.thumbnailUrl || ""}
                        altText=""
                        fetchpriority="low"
                        aspectRatio={236 / 133}
                    />
                    <div
                        className={styles.playIconWithDuration}
                        data-component="play-icon-with-duration"
                    >
                        <div className={styles.playIcon}>
                            <PlayIcon />
                        </div>
                        {typeof video.duration === "number" ? (
                            <div className={styles.duration}>
                                {convertMillisecondsToMinutesAndSecondsString(
                                    video.duration
                                )}
                            </div>
                        ) : null}
                    </div>
                    <div className={styles.gradient} />
                </div>
                <p className={styles.cardHeadline}>{video.headline}</p>
            </a>
        </div>
    );
};

const MoreLikeThisCard = ({ stories }: { stories: PersonalizationStory[] }) => {
    const onClick = useCallback(() => {
        tracker.trackEvent({
            category: "takeaways-card",
            label: "click-story",
        });
    }, []);
    return (
        <div
            className={styles.card}
            data-testid="carousel-personalization-card"
        >
            <h3 className={styles.cardTitle}>
                <TranslatedMessage
                    id="moreLikeThis"
                    defaultMessage="More Like This"
                />
            </h3>
            <ul className={styles.moreLikeThisListContainer}>
                {stories
                    .slice(0, MAX_PERSONALIZATION_HEADLINES)
                    .flatMap((story) => (
                        <li
                            key={story.headline}
                            className={styles.moreLikeThisCard}
                        >
                            <a
                                href={story.url}
                                target="_blank"
                                onClick={onClick}
                            >
                                <div className={styles.eyebrow}>
                                    {capitalizeDashedString(
                                        getPersonalizationSitesOverride(
                                            story.sites[0]
                                        )
                                    )}
                                </div>
                                <div className={styles.cardHeadline}>
                                    {story.headline}
                                </div>
                            </a>
                        </li>
                    ))}
            </ul>
        </div>
    );
};

const LoadingCard = () => (
    <div className={styles.card} data-testid="carousel-loading-card">
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
    </div>
);

type RenderCardParams =
    | {
          type: RenderCardType.AI_SUMMARY;
          aiSummaryText: string[];
      }
    | {
          type: RenderCardType.VIDEO;
          videos: PersonalizationStory[];
      }
    | {
          type: RenderCardType.PERSONALIZATION;
          stories: PersonalizationStory[];
      }
    | {
          type: RenderCardType.LOADING;
      };

const RenderCard = (props: RenderCardParams) => {
    switch (props.type) {
        case RenderCardType.AI_SUMMARY:
            return <AISummaryCard {...props} />;
        case RenderCardType.VIDEO:
            return <VideoCard {...props} />;
        case RenderCardType.PERSONALIZATION:
            return <MoreLikeThisCard {...props} />;
        case RenderCardType.LOADING:
            return <LoadingCard />;
        default:
            return null;
    }
};

const Takeaways: React.FC<TakeawaysProps> = ({ story }) => {
    const translateMessage = useTranslateMessage();
    const [isOpen, setIsOpen] = useState(true);
    const [isPersonalizationLoading, setIsPersonalizationLoading] =
        useState(false);
    const [personalizationError, setPersonalizationError] = useState();
    const [personalizedStories, setPersonalizedStories] = useState<
        PersonalizationStory[]
    >([]);
    const [videoPersonalizedStories, setPersonalizedVideoStories] = useState<
        PersonalizationStory[]
    >([]);
    const carouselRef = useRef<HTMLDivElement | null>(null);
    const [carouselRefState, setCarouselRefState] = useState(
        carouselRef.current
    );
    const [isLoaded, setLoaded] = useState(false);
    const fortressContext = useFortress();
    const { isBlocked: isFortressBlocked, isLoading: isFortressLoading } =
        getFortressState(fortressContext);
    useEffect(() => {
        if (isFortressBlocked) {
            setIsOpen(false);
        }

        const cookie = Cookie.get("_takeaways-open");
        if (cookie) {
            setIsOpen(cookie === "true");
        }
    }, [isFortressBlocked]);
    const PERSONALIZATION_LOADING_TIMEOUT = 3000;
    useEffect(() => {
        setIsPersonalizationLoading(true);
        const timer = setTimeout(
            () => setIsPersonalizationLoading(false),
            PERSONALIZATION_LOADING_TIMEOUT
        );
        Promise.allSettled([
            getPersonalizedStories(story),
            getVideoPersonalizedStories(story),
        ])
            .then(([personalizationStories, personalizationVideoStories]) => {
                clearTimeout(timer);

                if (personalizationStories.status === "fulfilled") {
                    setPersonalizedStories(personalizationStories.value);
                }

                if (personalizationVideoStories.status === "fulfilled") {
                    setPersonalizedVideoStories(
                        personalizationVideoStories.value
                    );
                }

                if (personalizationStories.status === "rejected") {
                    console.error(personalizationStories.reason);
                }

                if (personalizationVideoStories.status === "rejected") {
                    console.error(personalizationVideoStories.reason);
                }
                setIsPersonalizationLoading(false);
            })
            .catch((e) => {
                setPersonalizationError(e);
                setIsPersonalizationLoading(false);
            });

        return () => clearTimeout(timer);
    }, [story.aiSummary]);

    const onClick = useCallback(() => {
        setIsOpen((prev) => {
            const newState = !prev;
            Cookie.set("_takeaways-open", newState.toString());
            if (newState === false) {
                tracker.trackEvent({
                    category: "takeaways-collapse",
                    label: "collapse-takeaways",
                });
            }
            return newState;
        });
    }, []);

    /**
     * TODO: there seems to be some churn with updating intercom to the latest
     * as a result, in order to ship ai summaries we are implementing a hack
     * to track GTM events in nav-button click. We should revisit to allow
     * `Carousel` to accept callbacks for the nav button after shipping
     * */
    useGTMNavButtonsEffect({ deps: [carouselRefState, isOpen] });
    useGTMSwipeEffect({ deps: [carouselRefState, isOpen] });

    useEffect(() => {
        setTimeout(() => {
            setLoaded(true);
        }, 500);
    }, [setLoaded]);
    const isEmptyArray =
        Array.isArray(story.aiSummary?.text) &&
        story.aiSummary?.text.length === 0;
    if (!story.aiSummary || !story.aiSummary?.text || isEmptyArray) {
        return null;
    }
    const componentPropsList = getCards({
        aiSummary: story.aiSummary,
        ...(personalizationError
            ? {}
            : {
                  personalizedStories,
                  personalizedVideoStories: videoPersonalizedStories,
              }),
    });
    const aiSummaryCards = getCards({
        aiSummary: story.aiSummary,
    });
    const displayCards = isPersonalizationLoading
        ? [
              ...aiSummaryCards,
              {
                  type: RenderCardType.LOADING,
              },
              {
                  type: RenderCardType.LOADING,
              },
          ]
        : componentPropsList;
    const numberOfColumns = displayCards.length;
    if (isFortressLoading) {
        return (
            <div
                className={createClasses([
                    styles.takeaways,
                    {
                        [styles.takeaways__loaded]: isLoaded,
                        [styles.takeaways__closed]: !isOpen,
                    },
                ])}
            >
                <h3 className={styles.title}>
                    <div className={styles.titleContent}>
                        <Sparkle />
                        <span className="font-bold whitespace-pre">
                            {"Takeaways "}
                        </span>
                    </div>
                </h3>
            </div>
        );
    }

    if (isFortressBlocked) {
        return (
            <div
                className={createClasses([
                    styles.takeaways,
                    {
                        [styles.takeaways__loaded]: isLoaded,
                        [styles.takeaways__closed]: !isOpen,
                    },
                ])}
            >
                <h3 className={styles.title}>
                    <div className={styles.titleContent}>
                        <Sparkle />
                        <span className="font-bold whitespace-pre">
                            {"Takeaways "}
                        </span>
                    </div>
                    <button className={styles.titleButton}>
                        <div className={styles.lock}>
                            <Lock />
                        </div>
                        <span className="text-caption-xs underline">
                            <a
                                href="/subscriptions"
                                target="_blank"
                                className="text-caption-xs"
                            >
                                <TranslatedMessage
                                    id="subscribe"
                                    defaultMessage="Subscribe"
                                />
                            </a>
                        </span>
                    </button>
                </h3>
            </div>
        );
    }
    return (
        <div
            className={createClasses([
                styles.takeaways,
                {
                    [styles.takeaways__loaded]: isLoaded,
                    [styles.takeaways__closed]: !isOpen,
                },
            ])}
        >
            <h3 className={styles.title}>
                <div className={styles.titleContent}>
                    <Sparkle />
                    <span className="font-bold whitespace-pre">
                        {"Takeaways "}
                    </span>
                </div>
                <button className={styles.titleButton} onClick={onClick}>
                    <span className="text-caption-xs">
                        {isOpen
                            ? translateMessage({
                                  id: "hide",
                                  defaultMessage: "Hide",
                              })
                            : translateMessage({
                                  id: "show",
                                  defaultMessage: "Show",
                              })}
                    </span>
                    <div className={styles.arrow}>
                        {isOpen ? <ChevronUpThin /> : <ChevronDownThin />}
                    </div>
                </button>
            </h3>
            <div
                // TODO: this is a hack, see comment above regarding aiSummaries
                ref={(el) => {
                    carouselRef.current = el ?? null;
                    setCarouselRefState(carouselRef.current);
                }}
                className={createClasses([
                    styles.carousel,
                    {
                        [styles.isOpen]: isOpen,
                    },
                ])}
            >
                <div className={styles.desktop}>
                    <Carousel.Component
                        IterableComponent={RenderCard}
                        componentPropsList={
                            displayCards as unknown as RenderCardParams[]
                        }
                        numberOfColumns={numberOfColumns}
                        tileProperties={{
                            width: 260,
                            gap: 12,
                        }}
                    />
                </div>
                <div className={styles.mobile}>
                    <MobileCarousel
                        IterableComponent={RenderCard}
                        componentPropsList={
                            displayCards as unknown as RenderCardParams[]
                        }
                    />
                </div>
            </div>
        </div>
    );
};
export default Takeaways;
